import { CurrentPathStep, getCurrentPathStep } from './advancePathProgress';
import { LearningPathData, PathCheckpoint } from './types';

export type NextCheckpointCardModel = {
  goalTitle: string;
  nextCheckpoint: PathCheckpoint;
  /**
   * The checkpoint the current leg starts from — null on the first leg, where the
   * leg starts at the beginning of the path instead.
   */
  previousCheckpoint: PathCheckpoint | null;
  checkpointIndex: number;
  checkpointTotal: number;
  lessonsRemaining: number;
  /** Total sessions on the current leg (used for progress-track graduations). */
  totalSessions: number;
  /** 0–1 position of "You're here" within the current leg toward the next checkpoint. */
  progressRatio: number;
  currentStep: CurrentPathStep;
};

const isActiveSessionStatus = (status: string): boolean =>
  status === 'current' || status === 'upcoming';

/**
 * Builds view-model data for the Home Path cards.
 * Returns null when there is no upcoming/current checkpoint (path complete or empty).
 */
export const buildNextCheckpointCardModel = (
  pathData: LearningPathData,
): NextCheckpointCardModel | null => {
  const currentStep = getCurrentPathStep(pathData);
  if (!currentStep) {
    return null;
  }

  const nextCheckpointIndex = pathData.checkpoints.findIndex(
    checkpoint => checkpoint.status === 'current' || checkpoint.status === 'upcoming',
  );
  if (nextCheckpointIndex < 0) {
    return null;
  }

  const nextCheckpoint = pathData.checkpoints[nextCheckpointIndex];
  const previousCheckpoint =
    nextCheckpointIndex > 0 ? pathData.checkpoints[nextCheckpointIndex - 1] : null;
  const leg = pathData.legs[nextCheckpointIndex];
  const sessions = leg?.sessions ?? [];

  const lessonsRemaining =
    currentStep.kind === 'checkpoint' && currentStep.checkpoint.id === nextCheckpoint.id
      ? 0
      : sessions.filter(session => isActiveSessionStatus(session.status)).length;

  const completedCount = sessions.filter(session => session.status === 'completed').length;
  const totalSessions = sessions.length;

  let progressRatio = 0;
  if (currentStep.kind === 'checkpoint' && currentStep.checkpoint.id === nextCheckpoint.id) {
    progressRatio = 1;
  } else if (totalSessions > 0) {
    // Place "You're here" after completed sessions, at the current/first remaining lesson.
    const currentIndex = sessions.findIndex(session => session.status === 'current');
    if (currentIndex >= 0) {
      progressRatio = currentIndex / totalSessions;
    } else {
      progressRatio = completedCount / totalSessions;
    }
  }

  return {
    goalTitle: pathData.goal.title,
    nextCheckpoint,
    previousCheckpoint,
    checkpointIndex: nextCheckpointIndex + 1,
    checkpointTotal: pathData.checkpoints.length,
    lessonsRemaining,
    totalSessions,
    progressRatio,
    currentStep,
  };
};
